import { type VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import { LanguageSwitcher, Text, CloseIcon, TextLink } from '../../';
import type { CommonProps } from '../../types';
import type { NavigationItemData } from '@beat-venues/common-types';
import { ReactElement } from 'react';

export interface MobileMenuProps
    extends CommonProps,
        VariantProps<typeof MobileMenuStyles> {
    languageSwitcher: ReactElement<typeof LanguageSwitcher>;
    items: NavigationItemData[];
    logout: () => void;
    logoutLabel: string;
    open?: boolean;
    onClose?: () => void;
}

export const MobileMenuStyles = cva(
    'px-6 pb-14 bg-black h-screen w-100 fixed inset-0 lg:hidden overflow-auto z-20',
    {
        variants: {
            open: {
                true: 'block',
                false: 'hidden',
            },
        },
        defaultVariants: {
            open: false,
        },
    }
);

const renderMobileMenuItem = (
    item: NavigationItemData,
    onClickCallback?: () => void,
    IsSubMenuItem = false
) => (
    <TextLink
        className={`w-full
            ${
                IsSubMenuItem
                    ? `text-3xl leading-10 hover:no-underline`
                    : `text-4xl leading-[3rem] hover:no-underline`
            }`}
        href={item.href}
        underline={false}
        onClick={onClickCallback}
    >
        {item.label}
    </TextLink>
);
export const MobileMenu = ({
    languageSwitcher,
    items,
    open,
    onClose,
    className,
    logout,
    logoutLabel,
    testId = 'mobile-menu',
    ...props
}: MobileMenuProps): JSX.Element => {
    return (
        <div
            className={twMerge(MobileMenuStyles({ open, className }))}
            data-testid={testId}
            {...props}
        >
            <div className="flex justify-between bg-inherit pb-7 pt-16">
                {languageSwitcher}
                <button className="focus lg:hidden" onClick={onClose}>
                    <CloseIcon size="lg" />
                </button>
            </div>
            <nav role="navigation" aria-label="Main">
                <ul>
                    {items.map((item) => (
                        <li key={item.id} className="mb-0.5">
                            {item.href ? (
                                renderMobileMenuItem(item, onClose)
                            ) : (
                                <>
                                    <Text className="text-4xl leading-[3rem]">
                                        {item.label}
                                    </Text>
                                    {item.items && (
                                        <ul className="pt-4">
                                            {item.items?.length &&
                                                item.items.map((subItem) => (
                                                    <li
                                                        key={subItem.id}
                                                        className="mb-1"
                                                    >
                                                        {renderMobileMenuItem(
                                                            subItem,
                                                            onClose,
                                                            true
                                                        )}
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </nav>
            <TextLink
                className="w-full text-4xl leading-[3rem] no-underline"
                onClick={logout}
            >
                {logoutLabel}
            </TextLink>
        </div>
    );
};
